import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../utils/AxiosConfig";
import "../../Styles/userorder.css";
import { FiArrowLeft, FiCheckCircle, FiClock, FiCreditCard } from "react-icons/fi";

const TERMINAL_PAYMENT_STATUSES = new Set([
  "paid",
  "failed",
  "expired",
  "cancelled",
  "refunded",
]);

const normalizeStatus = (value) =>
  String(value || "pending")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const getReadableStatus = (value) => {
  const normalized = normalizeStatus(value);
  if (normalized === "paid") return "Paid";
  if (normalized === "pending") return "Pending";
  if (normalized === "failed") return "Failed";
  if (normalized === "expired") return "Expired";
  if (normalized === "cancelled") return "Cancelled";
  if (normalized === "refunded") return "Refunded";

  return normalized
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatDateTime = (value) => {
  if (!value) return "Not available";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not available";
  return parsed.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPaymentMethodLabel = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "Not specified";
  if (normalized === "stripe") return "Stripe Card Payment";
  if (normalized === "tamara") return "Tamara";
  if (normalized === "tabby") return "Tabby";
  if (normalized === "card") return "Card Payment";
  return normalized
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentDetail, setPaymentDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshCount, setRefreshCount] = useState(0);

  const orderId = Number(searchParams.get("order_id") || 0);
  const sessionId = searchParams.get("session_id") || "";
  const returnProvider = searchParams.get("provider") || "";
  const isTamaraPayment =
    String(paymentDetail?.payment_method || paymentDetail?.latest_payment?.provider || returnProvider)
      .trim().toLowerCase() === "tamara";
  const isTabbyPayment =
    String(paymentDetail?.payment_method || paymentDetail?.latest_payment?.provider || returnProvider)
      .trim().toLowerCase() === "tabby";
  const bnplProvider = isTabbyPayment ? "Tabby" : "Tamara";

  const loadPaymentDetail = useCallback(async ({ silent = false } = {}) => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      setErrorMessage("");
      if (returnProvider === "tamara") {
        try {
          await axiosInstance.post(`tamara/orders/${orderId}/refresh-status`);
        } catch {
          // The order payment endpoint below remains authoritative if Tamara is temporarily unavailable.
        }
      }
      if (returnProvider === "tabby") {
        try {
          await axiosInstance.post(`tabby/orders/${orderId}/refresh-status`);
        } catch {
          // The order payment endpoint remains the source of confirmed local status.
        }
      }
      const response = await axiosInstance.get(`user/orders/${orderId}/payment`);
      if (response.data?.success && response.data?.data) {
        setPaymentDetail(response.data.data);
      } else {
        throw new Error("Unable to confirm payment status.");
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || "Unable to confirm payment status right now.",
      );
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [orderId, returnProvider]);

  useEffect(() => {
    loadPaymentDetail();
  }, [loadPaymentDetail]);

  const paymentStatus = useMemo(
    () =>
      normalizeStatus(
        paymentDetail?.payment_status ||
          paymentDetail?.latest_payment?.status ||
          "pending",
      ),
    [paymentDetail],
  );

  useEffect(() => {
    if (
      !orderId ||
      loading ||
      TERMINAL_PAYMENT_STATUSES.has(paymentStatus) ||
      refreshCount >= 5
    ) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setRefreshCount((count) => count + 1);
      loadPaymentDetail({ silent: true });
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [loadPaymentDetail, loading, orderId, paymentStatus, refreshCount]);

  const title =
    paymentStatus === "paid"
      ? "Payment Received"
      : paymentStatus === "failed"
        ? "Payment Failed"
        : paymentStatus === "expired"
          ? "Payment Expired"
          : (isTamaraPayment || isTabbyPayment) && paymentStatus === "cancelled"
            ? "Payment Cancelled"
            : (isTamaraPayment || isTabbyPayment) && paymentStatus === "refunded"
              ? "Payment Refunded"
          : "Payment Processing";
  const subtitle =
    paymentStatus === "paid"
      ? (isTamaraPayment || isTabbyPayment)
        ? `${bnplProvider} has confirmed your payment and your order is now marked as paid.`
        : "Stripe has confirmed your payment and your order is now marked as paid."
      : paymentStatus === "failed"
        ? (isTamaraPayment || isTabbyPayment)
          ? `${bnplProvider} reported that this payment was declined.`
          : "The checkout was completed, but Stripe reported a payment failure."
        : paymentStatus === "expired"
          ? (isTamaraPayment || isTabbyPayment)
            ? `The ${bnplProvider} checkout expired before payment could be confirmed.`
            : "The Stripe checkout session expired before payment could be confirmed."
          : (isTamaraPayment || isTabbyPayment) && paymentStatus === "cancelled"
            ? `${bnplProvider} confirms this payment was cancelled. You can review the order before trying again.`
            : (isTamaraPayment || isTabbyPayment) && paymentStatus === "refunded"
              ? `${bnplProvider} confirms this payment was refunded.`
          : (isTamaraPayment || isTabbyPayment)
            ? `We are waiting for ${bnplProvider} to confirm your payment. You can refresh this status.`
            : "Your payment redirect was successful. We are waiting for Stripe webhook confirmation.";

  if (loading) {
    return (
      <div className="userorder-page">
        <div className="userorder-container">
          <div className="userorder-loading">
            <div className="loading-spinner"></div>
            <p>Confirming your payment status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="userorder-page">
      <div className="userorder-container">
        <section className="userorder-hero">
          <button
            className="userorder-back-btn"
            onClick={() => navigate("/account", { state: { navigationname: "orders" } })}
          >
            <FiArrowLeft /> Back to My Orders
          </button>
          <div className="userorder-hero-copy">
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
        </section>

        {errorMessage ? (
          <div className="userorder-inline-error">{errorMessage}</div>
        ) : null}

        <section className="userorder-card userorder-summary-card">
          <div className="summary-copy">
            <div className="summary-title-row">
              <h2>
                {paymentDetail?.order_number
                  ? `Order ${paymentDetail.order_number}`
                  : orderId
                    ? `Order #${orderId}`
                    : "Payment Status"}
              </h2>
              <span className={`payment-status-chip ${paymentStatus.replace(/_/g, "-")}`}>
                {getReadableStatus(paymentStatus)}
              </span>
            </div>
            <p className="summary-subtitle">
              {paymentStatus === "paid"
                ? "Your order is ready to continue through the normal fulfilment flow."
                : (isTamaraPayment || isTabbyPayment) && TERMINAL_PAYMENT_STATUSES.has(paymentStatus)
                  ? "View your order for the latest payment details."
                  : "Webhook confirmation can take a few seconds. This page refreshes automatically while the payment is pending."}
            </p>
          </div>

          <div className="summary-actions">
            {orderId ? (
              <button
                className="userorder-primary-btn"
                onClick={() => navigate(`/userorder/${orderId}`)}
              >
                <FiCheckCircle /> View Order
              </button>
            ) : null}
            <button
              className="userorder-secondary-btn"
              onClick={() => loadPaymentDetail()}
            >
              <FiClock /> Refresh Status
            </button>
          </div>
        </section>

        <section className="userorder-card">
          <div className="card-head">
            <div>
              <h3>Payment Tracking</h3>
            </div>
          </div>

          <div className="payment-panel">
            <div className="payment-row">
              <span className="payment-label">
                <FiCreditCard /> Method
              </span>
              <strong>
                {getPaymentMethodLabel(
                  paymentDetail?.payment_method ||
                    paymentDetail?.latest_payment?.payment_method,
                )}
              </strong>
            </div>
            <div className="payment-row">
              <span className="payment-label">Amount</span>
              <strong>
                AED {Number(paymentDetail?.amount || 0).toFixed(2)}
              </strong>
            </div>
            <div className="payment-row">
              <span className="payment-label">Status</span>
              <span className={`payment-status-chip ${paymentStatus.replace(/_/g, "-")}`}>
                {getReadableStatus(paymentStatus)}
              </span>
            </div>
            <div className="payment-row">
              <span className="payment-label">Last Updated</span>
              <strong>
                {formatDateTime(
                  paymentDetail?.latest_payment?.updated_at || paymentDetail?.updated_at,
                )}
              </strong>
            </div>
            {sessionId && !isTamaraPayment ? (
              <div className="payment-row">
                <span className="payment-label">Stripe Session</span>
                <strong>{sessionId}</strong>
              </div>
            ) : null}
            {paymentDetail?.latest_payment?.failure_message ? (
              <div className="tracking-fallback">
                {paymentDetail.latest_payment.failure_message}
              </div>
            ) : null}
            {paymentDetail?.latest_payment?.receipt_url ? (
              <div className="payment-row">
                <span className="payment-label">Receipt</span>
                <a
                  href={paymentDetail.latest_payment.receipt_url}
                  target="_blank"
                  rel="noreferrer"
                  className="userorder-secondary-btn"
                  style={{ textDecoration: "none" }}
                >
                  Open Receipt
                </a>
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}

export default PaymentSuccess;
